/**
 * scopingWidgetRepair — tactical resilience guard for scoping artifact widgets.
 *
 * On develop, scoping `sc_artifact_card` widgets are HAND-AUTHORED by the model
 * (the guide bakes each section's widget schema inline so the model never
 * round-trips `widget_catalog`; see server/services/scoping-context-brief.ts).
 * The model sometimes mis-emits them — a live staging call produced two failures:
 *
 *   Bug A — the `kind` discriminator was set to the OUTCOME_TYPE enum value
 *           ("GoNoGo") instead of the literal "decision", so the card fails the
 *           discriminated-union schema → renders as a broken "Unsupported widget".
 *   Bug B — a decision artifact was emitted under the WRONG `widget_type`
 *           (`sc_learnt_card`), so it renders as a stray learnt card (or fails).
 *
 * These are ANA-535-class model-emission-integrity failures. The long-term fix is
 * spec 004-p6 (ANA-586/587), which Cloud-materialises these cards from the
 * known-good `briefing_draft`. This transform is a TACTICAL guard that ships
 * ahead of it and degrades gracefully, and emits a shape-match validation signal
 * so we can measure how often the LLM mis-emits (feeding the 004-p6 case).
 *
 * It reuses the SAME per-kind artifacts the (always-accurate) scoping rail derives
 * from `briefing_draft` (see {@link deriveScopingArtifactsByKind}) as the
 * substitution source — so a repaired card shows the same known-good data the rail
 * shows, never an invented shape.
 *
 * Design: model card WHEN VALID, rail card WHEN BROKEN. A valid card is kept
 * verbatim so text mode still shows the per-turn accreting history; only a broken
 * card is replaced (with current rail state — a broken card had no usable at-turn
 * state to preserve anyway).
 */

import type { V2ConversationTurn, WidgetBlock, ContentBlock } from '@shared/conversation-schema';
import {
  ScArtifactCardPropsSchema,
  SC_ARTIFACT_KINDS,
  type ScArtifactCardProps,
  type ScArtifactKind,
} from '@shared/widget-schemas';
import type { ScopingRailRow } from './scopingRailModel';

const SC_ARTIFACT_CARD = 'sc_artifact_card';

/** Rail-derived, known-good artifact card per kind — the substitution source. */
export type ScopingArtifactFallback = Partial<Record<ScArtifactKind, ScArtifactCardProps>>;

/**
 * Bridge the (always-accurate) scoping rail rows into the by-kind fallback the
 * repair uses. Reuses the rail's own `briefing_draft` derivation verbatim — the
 * page already computes these rows via `buildScopingRailRowsFromContext`, so this
 * adds no second source of truth. The orient `note` is informational, never a
 * substitution source, so it's excluded.
 */
export function scopingArtifactFallbackFromRailRows(rows: ScopingRailRow[]): ScopingArtifactFallback {
  const out: ScopingArtifactFallback = {};
  for (const row of rows) {
    const cap = row.captured;
    if (!cap || cap.payload.kind === 'note') continue;
    out[cap.payload.kind] = { payload: cap.payload, draft: cap.artifactStatus === 'draft' };
  }
  return out;
}

/** What the guard did to one emitted artifact block — the validation signal. */
export interface ScopingWidgetRepairSignal {
  /** The resolved/inferred artifact kind, or null when it couldn't be determined. */
  kind: ScArtifactKind | null;
  /** True only when the model emitted a schema-valid `sc_artifact_card` (no repair). */
  matched: boolean;
  /** The repair applied. */
  action: 'kept' | 'retyped' | 'substituted' | 'dropped';
  /** The `widget_type` the model actually emitted (before any re-type). */
  widgetType: string;
  /** The block's stable id, paired with `turnId` as the once-per-emission dedupe
   *  key — the transform re-runs over every turn whenever a new turn arrives, so
   *  the caller must dedupe rather than count per-run. */
  widgetId: string;
  /** The turn the block was emitted in. */
  turnId: string;
}

/** Result of {@link repairScopingArtifactWidgets}: the repaired turn stream plus
 *  the per-block verdicts. Signals are RETURNED (not emitted via a callback) so
 *  the caller fires them from an effect — never from a render-phase memo. */
export interface ScopingWidgetRepairResult {
  turns: V2ConversationTurn[];
  signals: ScopingWidgetRepairSignal[];
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return !!x && typeof x === 'object' && !Array.isArray(x);
}

/** The raw `payload` object off a widget block's `data`, if present. */
function rawPayload(data: unknown): Record<string, unknown> | undefined {
  if (!isRecord(data)) return undefined;
  const p = data.payload;
  return isRecord(p) ? p : undefined;
}

/**
 * A widget block is a candidate scoping artifact when it is an `sc_artifact_card`
 * OR it carries an artifact-shaped `data.payload.kind`. Genuine `sc_learnt_card`
 * / `sc_choice_card` / … payloads never carry `payload.kind`, so this reliably
 * distinguishes a mis-typed artifact (Bug B) from a real card of another type.
 */
function isArtifactCandidate(block: WidgetBlock): boolean {
  if (block.widget_type === SC_ARTIFACT_CARD) return true;
  const p = rawPayload(block.data);
  return typeof p?.kind === 'string';
}

/**
 * Best-effort recovery of the intended artifact kind from the payload SHAPE when
 * the `kind` discriminator itself is untrustworthy (Bug A). Distinctive fields
 * map cleanly; a bare `{ items: [{text}] }` is ambiguous between bets and goals,
 * so it returns undefined (the block is dropped rather than guessed wrong).
 */
export function inferArtifactKind(payload: unknown): ScArtifactKind | undefined {
  if (!isRecord(payload)) return undefined;
  // Trust an already-valid discriminator first.
  if (typeof payload.kind === 'string' && (SC_ARTIFACT_KINDS as readonly string[]).includes(payload.kind)) {
    return payload.kind as ScArtifactKind;
  }
  if (typeof payload.intent === 'string') return 'decision';
  if (Array.isArray(payload.groups)) return 'assumptions';
  if (typeof payload.summary === 'string') return 'scope';
  if (typeof payload.text === 'string' && !Array.isArray(payload.items)) return 'note';
  // `items`-only payloads are ambiguous (bets vs goals) → don't guess.
  return undefined;
}

/** Per-block verdict without the turn/block ids the transform tags on. */
type CoreSignal = Omit<ScopingWidgetRepairSignal, 'widgetId' | 'turnId'>;

/**
 * Repair the scoping artifact widgets in a turn stream. Pure: returns the
 * original turn/array instances wherever nothing changed (referential stability
 * for React memoisation). Signals are RETURNED, not emitted — the caller fires
 * them from an effect and dedupes by `(turnId, widgetId)`, so a signal counts
 * once per emission rather than once per render. Only runs meaningfully in
 * scoping mode — callers gate on that. A turn whose only content was a dropped
 * malformed artifact is omitted entirely (no empty bubble shell downstream).
 */
export function repairScopingArtifactWidgets(
  turns: V2ConversationTurn[],
  fallback: ScopingArtifactFallback,
): ScopingWidgetRepairResult {
  const signals: ScopingWidgetRepairSignal[] = [];
  const out: V2ConversationTurn[] = [];
  for (const turn of turns) {
    let changed = false;
    const content: ContentBlock[] = [];
    for (const block of turn.content) {
      if (block.type !== 'widget' || !isArtifactCandidate(block)) {
        content.push(block);
        continue;
      }
      const result = repairBlock(block, fallback);
      signals.push({ ...result.signal, widgetId: block.widget_id, turnId: turn.turn_id });
      if (result.block === block) {
        content.push(block);
      } else if (result.block) {
        content.push(result.block);
        changed = true;
      } else {
        // dropped
        changed = true;
      }
    }
    if (!changed) {
      out.push(turn);
    } else if (content.length > 0) {
      out.push({ ...turn, content });
    }
    // else: repair emptied the turn (its only block was a dropped malformed
    // artifact) → omit it so the transcript doesn't render an empty bubble.
  }
  return { turns: out, signals };
}

/** Resolve one artifact block to: keep (same instance), replace, or drop (null). */
function repairBlock(
  block: WidgetBlock,
  fallback: ScopingArtifactFallback,
): { block: WidgetBlock | null; signal: CoreSignal } {
  const parsed = ScArtifactCardPropsSchema.safeParse(block.data);

  if (parsed.success) {
    const kind = parsed.data.payload.kind;
    if (block.widget_type === SC_ARTIFACT_CARD) {
      // Valid card, correct type → keep the model's per-turn card verbatim.
      return { block, signal: { kind, matched: true, action: 'kept', widgetType: block.widget_type } };
    }
    // Valid payload but wrong widget_type (Bug B) → re-type, keep the model data.
    return {
      block: { ...block, widget_type: SC_ARTIFACT_CARD },
      signal: { kind, matched: false, action: 'retyped', widgetType: block.widget_type },
    };
  }

  // Invalid payload (Bug A) → substitute the rail-materialised card of the
  // intended kind. If the kind can't be inferred or no rail card exists, drop the
  // block (a repaired-or-absent card beats a rendered "Unsupported widget").
  const inferred = inferArtifactKind(rawPayload(block.data));
  const railCard = inferred ? fallback[inferred] : undefined;
  if (inferred && railCard) {
    return {
      block: { ...block, widget_type: SC_ARTIFACT_CARD, data: railCard as unknown as Record<string, unknown> },
      signal: { kind: inferred, matched: false, action: 'substituted', widgetType: block.widget_type },
    };
  }
  return {
    block: null,
    signal: { kind: inferred ?? null, matched: false, action: 'dropped', widgetType: block.widget_type },
  };
}
